import {
  createContext,
  useContext,
  type ReactNode,
} from "react";
import { useLapTimer } from "../hooks/useLapTimer";
import { useTelemetryContext } from "./TelemetryContext";
import type { Lap, FinishLine, SectorLine, SectorSplit, LapTimerState } from "../types/lap";

interface LapTimerContextValue extends LapTimerState {
  reset: () => void;
  setActive: (active: boolean) => void;
  finishLineConfigured: boolean;
}

const LapTimerContext = createContext<LapTimerContextValue | null>(null);

export function LapTimerProvider({
  finishLine,
  sectorLines,
  onLapComplete,
  children,
}: {
  finishLine: FinishLine | null;
  sectorLines: SectorLine[];
  onLapComplete?: (lap: Lap) => void;
  children: ReactNode;
}) {
  const { gps } = useTelemetryContext();
  const lapTimer = useLapTimer(gps, finishLine, sectorLines, onLapComplete);
  return (
    <LapTimerContext.Provider value={{ ...lapTimer, finishLineConfigured: finishLine !== null }}>
      {children}
    </LapTimerContext.Provider>
  );
}

export function useLapTimerContext(): LapTimerContextValue {
  const ctx = useContext(LapTimerContext);
  if (!ctx) {
    throw new Error(
      "useLapTimerContext must be used within a LapTimerProvider",
    );
  }
  return ctx;
}

export type { LapTimerContextValue, SectorSplit };
